import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ArrowRight, ChevronDown, Lock, MapPin, Radar, Search, ShieldCheck } from "lucide-react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { BadgeStatus, type StatusKind } from "@/components/ui/badge-status";
import { fetchMunicipalities } from "@/api/client";
import {
  fetchRadarMeta,
  fetchRadarZone,
  radarQuery,
  radarVerdikt,
  type RadarGeometry,
  type RadarMeta,
  type RadarParsed,
  type RadarResult,
  type RadarZoneDetail,
} from "@/api/radar";

const EXAMPLES = [
  "gradivo obalno turističko do 25 ha, čisto kupivo",
  "turističko na Krku",
  "prazno poslovno zemljište do 10 ha",
  "obalno turističko, najveće",
];

const MACRO_LABEL: Record<string, string> = {
  turist: "turistička",
  naselje: "stambena",
  gospod: "poslovna / gospodarska",
  rekrea: "rekreacijska",
};

const KLASA_LABEL: Record<string, string> = {
  A_gradiv_UPU: "Gradivo · UPU donesen",
  B_aktivacija_UPU: "Aktivacija · UPU u izradi",
  C_bez_plana: "Rana zaliha · bez UPU-a",
};

function bendBadge(bend: string | null): { kind: StatusKind; label: string } | null {
  if (bend === "ČISTO") return { kind: "ok", label: "ČISTO kupivo" };
  if (bend === "SREDNJE") return { kind: "warn", label: "SREDNJE" };
  if (bend === "FRAGMENTIRANO") return { kind: "err", label: "FRAGMENTIRANO" };
  return null;
}

function hr(n: number | null | undefined, d = 1): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("hr-HR", { maximumFractionDigits: d });
}

function ParsedChips({ p }: { p: RadarParsed }) {
  const chips: string[] = [];
  if (p.opcina) chips.push(`općina: ${p.opcina}`);
  if (p.macro) chips.push(`namjena: ${MACRO_LABEL[p.macro] ?? p.macro}`);
  if (p.klasa) chips.push(KLASA_LABEL[p.klasa] ?? p.klasa);
  if (p.coastal) chips.push("obalno");
  if (p.ha_min != null) chips.push(`≥ ${hr(p.ha_min)} ha`);
  if (p.ha_max != null) chips.push(`≤ ${hr(p.ha_max)} ha`);
  if (p.bend) chips.push("čisto kupivo");
  if (!chips.length) return null;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-2">
      <span className="text-[0.72rem] uppercase tracking-[0.08em] text-stone-400">Razumio sam:</span>
      {chips.map((c) => (
        <span
          key={c}
          className="rounded border border-ind-100 bg-ind-50 px-2 py-0.5 text-[0.72rem] text-ind-700"
        >
          {c}
        </span>
      ))}
    </div>
  );
}

function norm(s: string): string {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z]/g, "");
}

// Handoff u Permitko /provjera — mapiranja dolaze s backenda (/api/radar/meta,
// LR-6: jedan izvor istine); ovo je LOKALNI FALLBACK ako fetch padne (offline UX).
const MACRO_TYPE_FALLBACK: Record<string, string> = {
  turist: "ugostiteljsko_turisticka",
  naselje: "visestambena",
  gospod: "poslovna",
  rekrea: "drustvena",
};

const T_SUBTYPE_FALLBACK: Record<string, string> = {
  "1": "hotel",
  "2": "turističko naselje",
  "3": "kamp",
};

function subtypeForOznaka(oznaka: string | null, tSubtype: Record<string, string>): string {
  const m = /^\s*T\s*(\d)/i.exec(oznaka ?? "");
  if (!m) return "";
  return tSubtype[m[1]] ?? "";
}

/**
 * Verdikt gradivosti — REUSE Permitko motora bez LLM-a: zove deterministički
 * /api/radar/zone/{id}/verdikt (get_evidence_bundle + numeric_checker), koji
 * bira pravilo po oznaci zone i vraća limite + pass/fail. Instant i točno.
 * Za neučitane općine: iskrena napomena (jedan izvor istine, ne dupliciramo).
 */
function VerdictSection({ zone }: { zone: RadarZoneDetail["zone"] }) {
  const navigate = useNavigate();
  const muniQuery = useQuery({ queryKey: ["municipalities"], queryFn: fetchMunicipalities });
  const metaQuery = useQuery<RadarMeta>({
    queryKey: ["radar-meta"],
    queryFn: fetchRadarMeta,
    staleTime: Infinity,
    retry: 1,
  });
  const macroType = metaQuery.data?.macro_type ?? MACRO_TYPE_FALLBACK;
  const tSubtype = metaQuery.data?.t_subtype ?? T_SUBTYPE_FALLBACK;
  const code = useMemo(() => {
    const list = muniQuery.data?.municipalities ?? [];
    const t = norm(zone.opcina);
    return list.find((m) => norm(m.name) === t || norm(m.code) === t)?.code ?? null;
  }, [muniQuery.data, zone.opcina]);

  const [proj, setProj] = useState({ kig: "", kisn: "", et: "", vis: "" });
  const verdikt = useMutation({
    mutationFn: (params: { kig?: number; kisn?: number; et?: number; vis?: number }) =>
      radarVerdikt(zone.id, params),
  });

  if (!code) {
    return (
      <div className="mt-2 rounded border-l-2 border-amber-400 bg-amber-50/70 px-3 py-2 text-[0.78rem] text-amber-800">
        {zone.verdikt_napomena}
      </div>
    );
  }

  const data = verdikt.data;
  const toNum = (s: string) => {
    const v = parseFloat(s.replace(",", "."));
    return Number.isFinite(v) ? v : undefined;
  };
  function check() {
    verdikt.mutate({ kig: toNum(proj.kig), kisn: toNum(proj.kisn), et: toNum(proj.et), vis: toNum(proj.vis) });
  }
  function openInPermitko() {
    navigate("/provjera", {
      state: {
        prefill: {
          building_type: macroType[zone.macro ?? ""] ?? "ugostiteljsko_turisticka",
          building_subtype: subtypeForOznaka(zone.oznaka, tSubtype),
          designation: zone.oznaka ?? "",
          municipality_code: code,
          work_type: "novogradnja",
        },
      },
    });
  }

  return (
    <div className="mt-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="rounded bg-green-50 px-1.5 py-0.5 text-[0.62rem] font-bold text-green-700">
          PERMITKO · plan učitan
        </span>
        {!data && (
          <Button size="sm" variant="outline" onClick={() => verdikt.mutate({})} disabled={verdikt.isPending}>
            <ShieldCheck className="h-3.5 w-3.5" />
            {verdikt.isPending ? "Provjeravam…" : "Provjeri gradivost"}
          </Button>
        )}
        {data?.outcome === "NEMA_PRAVILA" ? (
          // RB-2: bez pravila nema presude — neutralno (ne zeleno, ne crveno)
          <span className="inline-flex flex-shrink-0 items-center rounded border border-stone-200 bg-stone-100 px-1.5 py-0.5 text-[0.7rem] font-bold tracking-[0.02em] text-stone-600">
            NEMA PRAVILA
          </span>
        ) : (
          data?.outcome && (
            <BadgeStatus kind={data.outcome === "DOPUŠTENO" ? "ok" : "err"}>{data.outcome}</BadgeStatus>
          )
        )}
        <Button size="sm" variant="ghost" className="ml-auto" onClick={openInPermitko}>
          Puna provjera + dokumentacija
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {verdikt.error instanceof Error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[0.8rem] text-red-700">
          Greška: {verdikt.error.message}
        </div>
      )}

      {data?.outcome === "NEMA_PRAVILA" && (
        // RB-2: umjesto interaktivne provjere (koja bi opet vratila NEMA_PRAVILA)
        // neutralna uputa na ručnu provjeru
        <div className="rounded border-l-2 border-stone-300 bg-stone-50 px-3 py-2 text-[0.78rem] text-stone-600">
          {data.poruka ?? "Ručna provjera — nemamo učitana pravila za ovu općinu."}
        </div>
      )}

      {data && data.outcome !== "NEMA_PRAVILA" && (
        <>
          {data.limits.length > 0 && (
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
              <span className="text-[0.72rem] uppercase tracking-[0.06em] text-stone-400">Plan dopušta:</span>
              {data.limits.map((l) => (
                <span
                  key={l.label}
                  title={l.article ? `čl. ${l.article}` : undefined}
                  className="rounded bg-stone-100 px-2 py-0.5 font-mono text-[0.72rem] text-stone-700"
                >
                  {l.label} {l.op} {hr(l.value, 2)}
                  {l.unit}
                </span>
              ))}
              {data.limits[0]?.article && (
                <span className="text-[0.68rem] text-stone-400">čl. {data.limits[0].article}</span>
              )}
              {!data.subtype_matched && (
                <span className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[0.68rem] font-bold text-amber-800">
                  ⚠ najstroži kriterij — podtip zone nije prepoznat
                </span>
              )}
            </div>
          )}

          <div className="mb-2 flex flex-wrap items-end gap-2 rounded-md border border-stone-200 bg-stone-50/60 px-3 py-2">
            <span className="w-full text-[0.72rem] uppercase tracking-[0.06em] text-stone-400">
              Provjeri svoj projekt → DOPUŠTENO / BLOKADA:
            </span>
            {([["kig", "KIG"], ["kisn", "KISN"], ["et", "etaže"], ["vis", "visina m"]] as const).map(([k, lbl]) => (
              <label key={k} className="flex flex-col gap-0.5 text-[0.66rem] text-stone-500">
                {lbl}
                <Input
                  value={proj[k]}
                  onChange={(e) => setProj((p) => ({ ...p, [k]: e.target.value }))}
                  className="h-7 w-16 text-[0.8rem]"
                  inputMode="decimal"
                />
              </label>
            ))}
            <Button size="sm" onClick={check} disabled={verdikt.isPending}>
              {verdikt.isPending ? "…" : "Provjeri"}
            </Button>
          </div>

          {data.blockers.length > 0 && (
            <ul className="rounded-md border border-red-200 bg-red-50/50 px-3 py-1 text-[0.8rem]">
              {data.blockers.map((b) => (
                <li key={b.parameter} className="border-b border-red-100 py-1 text-red-800 last:border-0">
                  {b.parameter.toUpperCase()}: projekt {b.project_value} &gt; max {b.limit_value}
                  {b.source_article ? ` · čl. ${b.source_article}` : ""}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-1 font-mono text-[0.66rem] text-stone-400">
            pravilo: {data.rules_used.join(", ") || "—"} · motor (bez LLM-a)
          </div>
        </>
      )}
    </div>
  );
}

const BEND_COLOR: Record<string, string> = {
  "ČISTO": "#16a34a",
  "SREDNJE": "#d97706",
  "FRAGMENTIRANO": "#dc2626",
};

/** LR-3: zona na karti (Leaflet + OSM), poligon bojan po akvizibilnost-bendu. */
function ZoneMap({ geometry, bend }: { geometry: RadarGeometry; bend: string | null }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    const map = L.map(ref.current, { scrollWheelZoom: false });
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: "© OpenStreetMap",
    }).addTo(map);
    const color = BEND_COLOR[bend ?? ""] ?? "#4f46e5";
    const layer = L.geoJSON(geometry as GeoJSON.GeoJsonObject, {
      style: { color, weight: 2, fillColor: color, fillOpacity: 0.25 },
    }).addTo(map);
    map.fitBounds(layer.getBounds(), { padding: [14, 14] });
    return () => {
      map.remove();
    };
  }, [geometry, bend]);
  return (
    <div
      ref={ref}
      className="mb-3 h-60 w-full overflow-hidden rounded-md border border-stone-200"
      aria-label="Karta zone"
    />
  );
}

function ZoneDetail({ id }: { id: number }) {
  const q = useQuery({ queryKey: ["radar-zone", id], queryFn: () => fetchRadarZone(id) });
  if (q.isPending) return <div className="px-5 py-3 text-[0.8rem] text-stone-400">Učitavam detalj…</div>;
  if (q.isError || !q.data) return <div className="px-5 py-3 text-[0.8rem] text-red-600">Greška pri učitavanju.</div>;
  const { zone, brownfield_u_opcini } = q.data;
  return (
    <div className="border-t border-stone-100 bg-stone-50/60 px-5 py-4 text-[0.82rem]">
      {zone.geometry && <ZoneMap geometry={zone.geometry} bend={zone.bend} />}
      {zone.bend && (
        <div className="mb-2 text-stone-600">
          Akvizibilnost: <b>{zone.n_parcels}</b> čestica · <b>{zone.n_clusters}</b> matičnih grozdova ·
          najveći nosi <b>{hr(zone.top_cluster_pct, 0)}%</b> · {zone.n_blocks} blok(ova),
          najveći {hr(zone.max_block_ha)} ha
        </div>
      )}
      {((zone.ko && zone.ko.length > 0) || (zone.kc_primjeri && zone.kc_primjeri.length > 0)) && (
        <div className="mb-2 font-mono text-[0.74rem] text-stone-500">
          {zone.ko && zone.ko.length > 0 && <>k.o. {zone.ko.join(", ")} · </>}
          {zone.kc_primjeri && zone.kc_primjeri.length > 0 && (
            <>matične k.č. najvećih grozdova: {zone.kc_primjeri.slice(0, 8).join(", ")}</>
          )}
        </div>
      )}
      {brownfield_u_opcini.length > 0 && (
        <div className="mb-2">
          <span className="text-[0.72rem] uppercase tracking-[0.06em] text-stone-400">
            Brownfield u općini:
          </span>
          <ul className="mt-1 space-y-0.5">
            {brownfield_u_opcini.map((b) => (
              <li key={b.naziv} className="text-stone-600">
                • {b.naziv} ({hr(b.ha)} ha) — {b.planirana_namjena ?? "—"}
                {b.financijski_rizik ? ` · rizik: ${b.financijski_rizik}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      <VerdictSection zone={zone} />
    </div>
  );
}

function ResultCard({ r }: { r: RadarResult }) {
  const [open, setOpen] = useState(false);
  const badge = bendBadge(r.bend);
  return (
    <Card className="mb-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-start gap-3 px-5 py-4 text-left transition-colors hover:bg-stone-50"
      >
        <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-ind-600" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="font-bold text-stone-800">
              {(r.naselje || r.opcina)} · {r.oznaka}
            </span>
            <span className="font-mono text-[0.76rem] text-stone-500">{hr(r.ha)} ha</span>
            {r.coastal && (
              <span className="text-[0.7rem] text-teal-700">na obali</span>
            )}
            {badge && <BadgeStatus kind={badge.kind}>{badge.label}</BadgeStatus>}
            {r.evidence_tier === "V" && (
              <span className="rounded bg-stone-100 px-1.5 py-0.5 text-[0.62rem] font-bold text-stone-500">
                V · verificirano
              </span>
            )}
          </div>
          <div className="mt-1 text-[0.82rem] text-stone-500">
            {r.opcina} · {MACRO_LABEL[r.macro ?? ""] ?? r.detaljna_namjena ?? ""} ·{" "}
            {KLASA_LABEL[r.klasa ?? ""] ?? r.klasa}
            {r.plan_donesen ? ` (${r.plan_donesen})` : ""}
          </div>
          {badge && (
            <div className="mt-1 font-mono text-[0.72rem] text-stone-400">
              {r.n_parcels} čest · {r.n_clusters} grozdova · najveći {hr(r.top_cluster_pct, 0)}%
            </div>
          )}
        </div>
        <ChevronDown
          className={`mt-1 h-4 w-4 flex-shrink-0 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <ZoneDetail id={r.id} />}
    </Card>
  );
}

/**
 * Land Radar — zaključani modul (M2 chat-MVP). Korisnik upiše što traži; sustav
 * filtrira precomputani inventar (radar_*) i vraća analizu (zona + plan +
 * akvizibilnost), ČISTO-kupivo prvo. Bez WFS poziva na upit. Verdikt gradivosti
 * (C2) slijedi po općini (M3). Pristup je gated na backendu (PERMITKO_RADAR_OFFICES).
 */
export function RadarPage() {
  const [text, setText] = useState("");
  const search = useMutation({ mutationFn: (q: string) => radarQuery(q, 24) });

  function run(q: string) {
    const v = q.trim();
    if (!v) return;
    setText(v);
    search.mutate(v);
  }

  const data = search.data;
  return (
    <div className="mx-auto max-w-3xl">
      <div className="mb-1 flex items-center gap-2">
        <Radar className="h-5 w-5 text-ind-600" />
        <h1 className="text-[1.3rem] font-bold text-stone-800">Radar — pronađi teren</h1>
        <span className="ml-1 inline-flex items-center gap-1 rounded bg-stone-100 px-1.5 py-0.5 text-[0.62rem] font-bold text-stone-500">
          <Lock className="h-3 w-3" /> zaključano
        </span>
      </div>
      <p className="mb-5 text-[0.9rem] text-stone-500">
        Upiši što tražiš — radar filtrira de-riskirani obalni inventar (PGŽ) i vraća
        zone s namjenom, statusom plana i <b>akvizibilnošću po česticama</b>. Čisto kupivo prvo.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          run(text);
        }}
        className="mb-3 flex gap-2"
      >
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="npr. gradivo obalno turističko do 25 ha, čisto kupivo"
          className="flex-1"
        />
        <Button type="submit" disabled={search.isPending}>
          <Search className="h-4 w-4" />
          {search.isPending ? "Tražim…" : "Pretraži"}
        </Button>
      </form>

      <div className="mb-6 flex flex-wrap gap-2">
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            type="button"
            onClick={() => run(ex)}
            className="rounded-full border border-stone-200 bg-white px-3 py-1 text-[0.74rem] text-stone-600 transition-colors hover:border-ind-300 hover:text-ind-700"
          >
            {ex}
          </button>
        ))}
      </div>

      {search.error instanceof Error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[0.85rem] text-red-700">
          {search.error.message.includes("403")
            ? "Radar modul nije omogućen za ovaj pristup."
            : `Greška: ${search.error.message}`}
        </div>
      )}

      {data && (
        <>
          <ParsedChips p={data.parsed} />
          <div className="mb-3 text-[0.82rem] text-stone-500">
            {data.count === 0
              ? "Nema poklapanja — pokušaj šire (npr. makni ograničenje veličine ili 'čisto')."
              : `${data.count} ${data.count === 1 ? "zona" : "zona"} — čisto kupivo prvo. Klikni za detalj.`}
          </div>
          {data.results.map((r) => (
            <ResultCard key={r.id} r={r} />
          ))}

          {data.caveats.length > 0 && (
            <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-[0.8rem] text-amber-900">
              <div className="mb-1 font-bold">Iskrene ograde</div>
              <ul className="space-y-1">
                {data.caveats.map((c) => (
                  <li key={c}>• {c}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}
